/**
 * MDLR Product Form
 *
 * A custom element that wraps Shopify product forms and handles
 * add-to-cart functionality with proper validation and AJAX submission.
 *
 * This component:
 * - Validates form inputs before submission
 * - Prevents default form behaviour
 * - Calls MDLR.Cart.addItemFromForm()
 * - Dispatches events for other components to react to
 *
 * HTML Usage:
 *   <product-form>
 *     {% form 'product', product %}
 *       <select name="id">
 *         {% for variant in product.variants %}
 *           <option value="{{ variant.id }}">{{ variant.title }}</option>
 *         {% endfor %}
 *       </select>
 *       <input type="number" name="quantity" value="1">
 *       <button type="submit">Add to basket</button>
 *     {% endform %}
 *   </product-form>
 */

class ProductForm extends window.MDLR.CustomElement {
  /**
   * setupListeners: Attach form submit handler and cart event listeners
   * We intercept the submit event to add AJAX functionality
   * We also listen to cart events to update button states
   */
  setupListeners() {
    // Call parent setupListeners in case it does anything
    super.setupListeners();

    // Bind all handlers to maintain 'this' context
    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.onBeforeAdd = this.onBeforeAdd.bind(this);
    this.onAfterAdd = this.onAfterAdd.bind(this);

    // Attach submit listener to the form
    this.listeners.add(this.form, "submit", this.onSubmitHandler);

    // Listen to cart events for button state updates
    window.MDLR.eventBus.addEventListener(
      window.MDLR.EVENTS.CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeAdd,
    );

    window.MDLR.eventBus.addEventListener(
      window.MDLR.EVENTS.CART.AFTER_ADD_FROM_FORM,
      this.onAfterAdd,
    );

    // Listen for DOMContentLoaded
    if (document.readyState === "loading") {
      this.listeners.add(window, "DOMContentLoaded", () => {
        this.checkIfInCart();
      });
    } else {
      this.checkIfInCart();
    }
  }

  /**
   * checkValidity: Ensure this element wraps a valid product form
   * Throws an error if no form is found
   */
  checkValidity() {
    // Call parent validation first
    super.checkValidity();

    // Check that a form exists inside this element
    if (!this.form) {
      this.throwError("needs to be wrapped around a product's <form> element");
    }
  }

  /**
   * onSubmitHandler: Handle form submission
   * This runs when the user clicks the submit button
   *
   * @param {Event} e - Submit event
   */
  async onSubmitHandler(e) {
    this.log("submit", "Form submit handler triggered");

    // Prevent the default form submission
    // We'll handle it via AJAX instead
    e.preventDefault();

    // Check if all form inputs are valid
    // This uses the browser's built-in validation (required, min, max, pattern, etc.)
    let formIsValid = true;
    const invalidInputs = [];

    Array.from(this.form.elements).forEach((input) => {
      if (!input.validity.valid) {
        formIsValid = false;
        invalidInputs.push({
          name: input.name,
          type: input.type,
          message: input.validationMessage,
        });
      }
    });

    this.log(
      "validation",
      { formIsValid, invalidInputCount: invalidInputs.length },
      invalidInputs,
    );

    // If valid, add to cart via AJAX
    // If invalid, show browser validation messages
    if (formIsValid) {
      await this.addToCart();
    } else {
      // This shows the browser's built-in validation UI
      // (e.g., "Please fill out this field")
      this.log("validation", "validation failed, showing browser UI");
      this.form.reportValidity();
    }
  }

  /**
   * addToCart: Submit the form via AJAX
   * This calls the MDLR Cart API which handles the actual request
   *
   * @returns {Promise<Object>} - Cart state from Shopify
   */
  async addToCart() {
    this.log("addToCart", "Starting add to cart process");
    try {
      // Call the MDLR Cart system
      // This handles the AJAX request, dispatches events, etc.
      this.log("addToCart", "Calling MDLR.Cart.addItemFromForm()");
      const result = await window.MDLR.Cart.addItemFromForm(this.form);
      this.log("addToCart", "Add to cart successful, result:", result);
      return result;
    } catch (error) {
      // Handle errors (out of stock, invalid variant, etc.)
      this.log("addToCart", "error caught:", error);
      this.onError(error);
      throw error;
    }
  }

  /**
   * checkIfInCart: Check if the current product/variant is in the cart
   * Updates the button state accordingly
   */
  async checkIfInCart() {
    this.log("checkIfInCart", "Checking if product is in cart");

    // Get the variant ID from the form
    const variantId = this.form?.querySelector('[name="id"]')?.value;
    if (!variantId) {
      this.log("checkIfInCart", "No variant ID found in form, skipping check");
      return;
    }

    try {
      const cart = await window.MDLR.Cart.get();
      cart.items.some((item) => {
        if (item.variant_id.toString() === variantId.toString()) {
          this.log("checkIfInCart", "Item is in cart:", item);
          const button = this.submitButton;
          button.textContent = "Added!";
          button.classList.add("product-form__button--added");
          button.dataset.cursor = "-inverse";
          button.disabled = true;
        }
      });
    } catch (error) {
      this.log("checkIfInCart", "Error fetching cart:", error);
    }
  }

  /**
   * onBeforeAdd: Handle the before add event
   * Show loading state on the button
   *
   * @param {CustomEvent} event - The before add event
   */
  onBeforeAdd(event) {
    this.log("events", "BEFORE_ADD_FROM_FORM event received");

    // Only update if this is our form being submitted
    if (event.detail.form !== this.form) {
      this.log("events", "Event form does not match this form, skipping");
      return;
    }

    const button = this.submitButton;
    if (!button) {
      this.log("events", "Submit button not found");
      return;
    }

    this.log("events", "Updating button to loading state");
    // Disable button and show loading state
    button.disabled = true;
    button.dataset.originalText = button.textContent;
    button.textContent = "Adding...";
  }

  /**
   * onAfterAdd: Handle the successful add event
   * Show success state on the button, then reset after delay
   *
   * @param {CustomEvent} event - The after add event
   */
  onAfterAdd(event) {
    this.log("events", "AFTER_ADD_FROM_FORM event received", event.detail);

    // Only update if this is our form being submitted
    if (event.detail.form !== this.form) {
      this.log("events", "Event form does not match this form, skipping");
      return;
    }

    const button = this.submitButton;
    if (!button) {
      this.log("events", "Submit button not found");
      return;
    }

    this.log("events", "Showing success state on button");
    // Show success state and keep it
    button.textContent = "Added!";
    button.classList.add("product-form__button--added");
  }

  /**
   * onError: Handle add to cart errors
   * Reset button to original state
   *
   * @param {Error} error - The error that occurred
   */
  onError(error) {
    this.log("error", "Add to cart error:", error, { stack: error.stack });
    console.error(error);

    const button = this.submitButton;
    if (!button) {
      this.log("error", "Submit button not found during error handling");
      return;
    }

    this.log("error", "Resetting button state after error");
    // Reset button state
    button.disabled = false;
    button.textContent = button.dataset.originalText || "Add to basket";
    button.classList.remove("product-form__button--added");
    delete button.dataset.originalText;

    // You could show an error notification here
    console.error("Add to cart error:", error);
  }

  /**
   * Get the form element inside this component
   * Returns the first <form> element found
   *
   * @returns {HTMLFormElement|null}
   */
  get form() {
    return this.querySelector("form");
  }

  /**
   * Get the submit button inside the form
   * Looks for button[type="submit"] or just button
   *
   * @returns {HTMLButtonElement|null}
   */
  get submitButton() {
    return (
      this.form?.querySelector('button[type="submit"]') ||
      this.form?.querySelector("button")
    );
  }
}

// Register the custom element
// Now you can use <product-form> in your Liquid templates
if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}
